// Login.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const Login = () => {
  const [username, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(null);
  const [companyLegalName, setCompanyLegalName] = useState(null);
  const [logoImageFailed, setLogoImageFailed] = useState(false);
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const token = localStorage.getItem('token');

  const loadBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/settings/branding`);
      if (res.data?.success) {
        setBrandingLogoUrl(res.data.logo_url || null);
        setCompanyLegalName(res.data.company_legal_name || null);
        return;
      }
    } catch {
      /* try legacy */
    }
    try {
      const res = await axios.get(`${apiUrl}/settings/logo`);
      if (res.data?.logo_url) {
        setBrandingLogoUrl(res.data.logo_url);
      }
    } catch {
      /* keep prior / empty */
    }
  }, []);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  useEffect(() => {
    if (token) {
      navigate('/dashboard', { replace: true });
    } else {
      logout();
    }
  }, [token, navigate, logout]);

  useEffect(() => {
    setLogoImageFailed(false);
  }, [brandingLogoUrl]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch(`${apiUrl}/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      if (response.ok) {
        login(username, data.token);
        const redirect = localStorage.getItem('postLoginRedirect');
        if (redirect) {
          localStorage.removeItem('postLoginRedirect');
          navigate(redirect);
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(data.message || 'Invalid credentials');
      }
    } catch (err) {
      setError('An error occurred during login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 sm:px-6">
      {/*
        Background: vertical cool gradient + soft orbs (mockup-style) + very subtle grid.
        Layer order: base gradient → orbs → grid → light vignette.
      */}
      <div
        className="fixed inset-0 -z-10 bg-gradient-to-b from-slate-400/70 from-0% via-slate-200 via-45% to-indigo-100/90 to-100%"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
        aria-hidden
      >
        <div className="absolute -top-32 -right-8 h-[22rem] w-[22rem] rounded-full bg-indigo-300/40 blur-3xl sm:h-[28rem] sm:w-[28rem]" />
        <div className="absolute -bottom-40 -left-20 h-[26rem] w-[26rem] rounded-full bg-sky-200/50 blur-3xl sm:-bottom-32 sm:left-0" />
        <div className="absolute top-1/2 left-1/2 h-[18rem] w-[20rem] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-100/35 blur-3xl" />
        <div className="absolute right-0 bottom-1/4 h-64 w-64 rounded-full bg-slate-300/20 blur-2xl" />
      </div>
      {/*
        Geometric accents: flat shapes + light shading (above orbs, under grid).
      */}
      <div
        className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
        aria-hidden
      >
        <div className="absolute -left-10 top-[10%] h-44 w-44 rotate-6 rounded-3xl bg-gradient-to-br from-white/20 to-slate-500/5 shadow-md ring-1 ring-white/25 sm:-left-8 sm:h-52 sm:w-52" />
        <div className="absolute -left-4 top-[18%] h-20 w-20 rotate-12 rounded-2xl border border-slate-600/8 bg-slate-800/5" />
        <div className="absolute right-[5%] top-[6%] h-28 w-28 rotate-45 rounded-2xl border-2 border-indigo-500/12 bg-indigo-400/5 shadow-sm sm:top-[8%] sm:h-36 sm:w-36" />
        <div className="absolute right-[14%] top-[20%] h-12 w-12 rotate-6 rounded-lg bg-sky-400/10 ring-1 ring-sky-300/20" />
        <div className="absolute -right-12 bottom-[14%] h-64 w-64 rounded-full border-2 border-dashed border-white/20 bg-gradient-to-tl from-indigo-200/5 to-transparent sm:bottom-[18%] sm:h-80 sm:w-80" />
        <div className="absolute -right-5 bottom-[22%] h-36 w-36 rounded-full border border-slate-500/10 bg-slate-600/5 shadow-inner" />
        <div className="absolute bottom-[30%] left-1/2 h-0 w-0 -translate-x-1/2 border-l-[3rem] border-l-transparent border-r-[3rem] border-r-transparent border-b-[2.5rem] border-b-slate-500/8 sm:border-b-[3rem] sm:border-l-[3.5rem] sm:border-r-[3.5rem]" />
        <div
          className="absolute left-[-20%] top-[42%] w-[140%] -translate-y-1/2 rotate-[-7deg] bg-gradient-to-r from-transparent via-slate-500/8 to-transparent"
          style={{ height: "5.5rem" }}
        />
        <div
          className="absolute right-[-15%] top-[60%] w-[55%] translate-y-1/2 rotate-[11deg] rounded-full bg-gradient-to-l from-sky-300/10 via-transparent to-transparent"
          style={{ height: "3rem" }}
        />
        <div className="absolute left-[8%] top-[32%] h-2.5 w-2.5 rounded-full bg-indigo-600/35 shadow-sm" />
        <div className="absolute left-[12%] top-[40%] h-1.5 w-1.5 rounded-full bg-slate-800/20" />
        <div className="absolute right-[18%] top-[45%] h-2 w-2 rounded-full border border-white/35 bg-white/10" />
        <div className="absolute bottom-24 right-[32%] h-3 w-3 rounded-sm rotate-45 border border-slate-600/15 bg-slate-500/10" />
        <div className="absolute bottom-0 right-[18%] h-16 w-40 rounded-t-2xl border border-b-0 border-slate-500/8 bg-gradient-to-b from-slate-600/5 to-transparent" />
        <div className="absolute bottom-8 left-[6%] h-10 w-24 rounded-t-xl bg-slate-800/5 ring-1 ring-slate-500/8" />
      </div>
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-70
          [background-image:linear-gradient(rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.04)_1px,transparent_1px)]
          [background-size:36px_36px]
        "
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 -z-10
          [background:radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(255,255,255,0.25)_0%,transparent_65%)]
        "
        aria-hidden
      />

      <div className="relative w-full max-w-md">
        <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_25px_50px_-12px_rgba(15,23,42,0.12)] sm:rounded-3xl">
          <div className="border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/60 px-8 pb-6 pt-8 sm:px-10 sm:pt-10">
            <div className="flex flex-col items-center text-center">
              {brandingLogoUrl && !logoImageFailed ? (
                <img
                  key={brandingLogoUrl}
                  src={brandingLogoUrl}
                  alt={companyLegalName || 'Company logo'}
                  onError={() => setLogoImageFailed(true)}
                  className="h-12 max-h-14 w-auto max-w-[220px] object-contain sm:h-14"
                />
              ) : (
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-800 text-white shadow-md">
                  <svg
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.75"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </div>
              )}
              {companyLegalName && (
                <p className="mt-4 text-base font-semibold tracking-tight text-slate-800">
                  {companyLegalName}
                </p>
              )}
            </div>
          </div>

          <div className="px-8 pb-8 pt-2 sm:px-10 sm:pb-10">
            <h1 className="text-center text-2xl font-bold tracking-tight text-slate-900">
              Sign in
            </h1>
            <p className="mt-1.5 text-center text-sm leading-relaxed text-slate-500">
              Access your rental inventory and dashboard
            </p>

            <form onSubmit={handleLogin} className="mt-7 space-y-4">
              <div>
                <label
                  htmlFor="login-email"
                  className="mb-1.5 block text-left text-sm font-medium text-slate-700"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={username}
                  onChange={(e) => setEmail(e.target.value)}
                  required
className="w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-slate-900 shadow-inner shadow-slate-200/20 transition focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200/60"
                placeholder="you@example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="mb-1.5 block text-left text-sm font-medium text-slate-700"
                >
                  Password
                </label>
                <input
                  id="login-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
className="w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-slate-900 shadow-inner shadow-slate-200/20 transition focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200/60"
                placeholder="••••••••"
                />
              </div>

              {error && (
                <div
                  className="rounded-lg border border-red-200/80 bg-red-50/90 px-3 py-2.5 text-center text-sm text-red-800"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="mt-1 w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/20 transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Sign in
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-slate-500">
              Rental inventory, quotes, and orders in one place.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
